import type { Card, CardTopupQuote } from './card.types';

export const CARD_QUOTE_TTL_SECONDS = 60;

export interface CardTopupMinimum {
  /** Minimum debit from the wallet, raw USDT — what the "you pay" field is validated against. */
  sourceRaw: bigint;
  /** ≈ what lands on the card at the minimum, card-currency minor units; null — no FX rate. */
  fiatMinor: bigint | null;
}

/**
 * §17.2: the minimum debit comes ready-made — it includes the live merchant deposit fee, so it
 * must never be derived from `net` + fee on the client. The quote wins over the card snapshot;
 * null from both means "unknown" and must not block the form (the backend answers BELOW_MIN).
 */
export function cardTopupMinimum(
  card: Card | null,
  quote: CardTopupQuote | null,
): CardTopupMinimum | null {
  if (quote && quote.minSourceAmountRaw !== null) {
    return { sourceRaw: quote.minSourceAmountRaw, fiatMinor: quote.minFiatAmountMinor };
  }
  if (card && card.minTopUpSourceAmountRaw !== null) {
    return { sourceRaw: card.minTopUpSourceAmountRaw, fiatMinor: card.minTopUpFiatAmountMinor };
  }
  return null;
}

/**
 * §17.2.4: upper bound of the processing time in whole minutes — 15 on the direct TRON path,
 * 30 via the bridge. Measured from the quote's own issue time (`expiresAt` − TTL), both server
 * timestamps, so client clock skew does not shift the promise.
 */
export function cardTopupCompletionMinutes(quote: CardTopupQuote | null): number | null {
  if (!quote) return null;
  const issuedAt = new Date(quote.expiresAt).getTime() - CARD_QUOTE_TTL_SECONDS * 1000;
  const minutes = Math.ceil((new Date(quote.estimatedCompletionAt).getTime() - issuedAt) / 60000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}
